import { Customer } from './customers';
import { CommunicationCard, Message } from '../types/messages';
import { supabase } from './supabase';
import { isCoreMessagesEnabled, fetchCoreThread, emailKey, phoneKey } from './messagesCore';

/** Core tables: one thread call gives the card its recent messages and unread state. */
async function coreCardFromThread(
  businessId: string,
  conversationKey: string,
  base: Omit<CommunicationCard, 'messages' | 'last_message' | 'unread_count' | 'has_unread'>,
  emptyTo: string,
  emptyType: Message['message_type'],
): Promise<CommunicationCard> {
  const page = await fetchCoreThread(businessId, conversationKey, { limit: 50, offset: 0 });
  // Every row, not the collapsed bubbles: email thread views read the full list.
  const messages: Message[] = page.messages;
  const lastMessage: Message = messages[0] || {
    id: 'empty',
    business_id: businessId,
    customer_id: base.customer_id ?? null,
    message_type: emptyType,
    direction: 'outbound',
    from_number: '',
    to_number: emptyTo,
    body: 'No messages yet',
    read: 'read',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  const unreadCount = messages.filter((m) => m.read === 'unread' && m.direction === 'inbound').length;
  return { ...base, messages, last_message: lastMessage, unread_count: unreadCount, has_unread: unreadCount > 0 };
}

/**
 * Creates a CommunicationCard from a customer for use in messaging
 */
export async function createCommunicationCardFromCustomer(
  customer: Customer,
  businessId: string
): Promise<CommunicationCard> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  if (await isCoreMessagesEnabled(businessId)) {
    return coreCardFromThread(
      businessId,
      `customer:${customer.id}`,
      {
        id: `customer_${customer.id}`,
        type: 'customer',
        customer_id: customer.id,
        customer_name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.email || customer.phone || 'Unknown',
        contact_info: customer.phone || customer.email || '',
      },
      customer.phone || '',
      'sms',
    );
  }

  // Fetch recent messages for this customer
  const { data: messagesData, error: messagesError } = await supabase
    .from('all_messages')
    .select('*')
    .eq('business_id', businessId)
    .eq('customer_id', customer.id)
    .order('created_at', { ascending: false })
    .limit(50); // Get recent messages

  if (messagesError) {
    console.error('Error fetching messages for customer:', messagesError);
  }

  // Map database messages to Message interface
  const messages: Message[] = (messagesData || []).map((msg: any) => ({
    ...msg,
    body: msg.body || msg.content || '',
    media_urls: msg.media_urls 
      ? (Array.isArray(msg.media_urls) 
          ? msg.media_urls 
          : JSON.parse(msg.media_urls || '[]'))
      : null
  }));

  // Get call history for this customer
  const { data: callsData } = await supabase
    .from('call_history')
    .select('*')
    .eq('business_id', businessId)
    .eq('customer_id', customer.id)
    .order('call_time', { ascending: false })
    .limit(50);

  // Convert calls to message format
  const callMessages: Message[] = (callsData || []).map((call: any) => ({
    id: call.id,
    business_id: call.business_id,
    customer_id: call.customer_id,
    message_type: 'call',
    direction: call.direction,
    from_number: call.from_number,
    to_number: call.to_number,
    body: call.direction === 'outbound' ? 'Outbound call' : 
      (call.status === 'answered' ? 'Call answered' : 'Missed call'),
    read: 'read',
    created_at: call.call_time,
    updated_at: call.updated_at || call.call_time,
    call_status: call.status,
    duration_seconds: call.duration_seconds,
    call_sid: call.call_sid,
    viewed: call.viewed
  }));

  // Combine all communications
  const allCommunications = [...messages, ...callMessages];

  // Find the most recent message
  const sortedCommunications = allCommunications.sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const lastMessage = sortedCommunications[0] || {
    id: 'empty',
    business_id: businessId,
    customer_id: customer.id,
    message_type: 'sms' as const,
    direction: 'outbound' as const,
    from_number: '',
    to_number: customer.phone || '',
    body: 'No messages yet',
    read: 'read' as const,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  // Count unread messages
  const unreadCount = allCommunications.filter(
    msg => msg.read === 'unread' && msg.direction === 'inbound'
  ).length;

  // Create the communication card
  const card: CommunicationCard = {
    id: `customer_${customer.id}`,
    type: 'customer',
    customer_id: customer.id,
    customer_name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.email || customer.phone || 'Unknown',
    contact_info: customer.phone || customer.email || '',
    messages: allCommunications,
    last_message: lastMessage,
    unread_count: unreadCount,
    has_unread: unreadCount > 0
  };

  return card;
}

/**
 * Creates a CommunicationCard from a phone number or email
 */
export async function createCommunicationCardFromContact(
  contactInfo: string,
  businessId: string,
  type: 'phone' | 'email' = 'phone'
): Promise<CommunicationCard> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  if (await isCoreMessagesEnabled(businessId)) {
    const key = type === 'email'
      ? `email:${emailKey(contactInfo) || ''}`
      : `phone:${phoneKey(contactInfo) || contactInfo.replace(/\D/g, '')}`;
    return coreCardFromThread(
      businessId,
      key,
      { id: `${type}_${contactInfo}`, type, contact_info: contactInfo },
      contactInfo,
      type === 'email' ? 'email' : 'sms',
    );
  }

  let messagesData: any[] = [];
  let callsData: any[] = [];

  if (type === 'phone') {
    // Build phone variants to match all common formats
    const digits = contactInfo.replace(/\D/g, '');
    const phoneVariants = new Set([contactInfo, digits]);
    if (digits.length === 10) {
      phoneVariants.add(`+1${digits}`);
      phoneVariants.add(`1${digits}`);
    } else if (digits.length === 11 && digits.startsWith('1')) {
      phoneVariants.add(`+${digits}`);
      phoneVariants.add(digits.slice(1));
    }
    const phoneOr = Array.from(phoneVariants)
      .flatMap(p => [`from_number.eq.${p}`, `to_number.eq.${p}`])
      .join(',');

    const { data, error } = await supabase
      .from('all_messages')
      .select('*')
      .eq('business_id', businessId)
      .eq('message_type', 'sms')
      .is('customer_id', null)
      .or(phoneOr)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error fetching messages:', error);
    } else {
      messagesData = data || [];
    }

    const { data: calls, error: callsError } = await supabase
      .from('call_history')
      .select('*')
      .eq('business_id', businessId)
      .or(phoneOr)
      .order('call_time', { ascending: false })
      .limit(50);

    if (callsError) {
      console.error('Error fetching calls:', callsError);
    } else {
      callsData = calls || [];
    }
  } else {
    // Email messages
    const { data, error } = await supabase
      .from('all_messages')
      .select('*')
      .eq('business_id', businessId)
      .eq('message_type', 'email')
      .is('customer_id', null)
      .or(`from_number.eq.${contactInfo},to_number.eq.${contactInfo}`)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error fetching email messages:', error);
    } else {
      messagesData = data || [];
    }
  }

  // Map messages
  const messages: Message[] = messagesData.map((msg: any) => ({
    ...msg,
    body: msg.body || msg.content || '',
    media_urls: msg.media_urls 
      ? (Array.isArray(msg.media_urls) 
          ? msg.media_urls 
          : JSON.parse(msg.media_urls || '[]'))
      : null
  }));

  // Convert calls to message format
  const callMessages: Message[] = callsData.map((call: any) => ({
    id: call.id,
    business_id: call.business_id,
    customer_id: call.customer_id,
    message_type: 'call',
    direction: call.direction,
    from_number: call.from_number,
    to_number: call.to_number,
    body: call.direction === 'outbound' ? 'Outbound call' : 
      (call.status === 'answered' ? 'Call answered' : 'Missed call'),
    read: 'read',
    created_at: call.call_time,
    updated_at: call.updated_at || call.call_time,
    call_status: call.status,
    duration_seconds: call.duration_seconds,
    call_sid: call.call_sid,
    viewed: call.viewed
  }));

  const allCommunications = [...messages, ...callMessages];

  const sortedCommunications = allCommunications.sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const lastMessage = sortedCommunications[0] || {
    id: 'empty',
    business_id: businessId,
    customer_id: null,
    message_type: type,
    direction: 'outbound' as const,
    from_number: '',
    to_number: contactInfo,
    body: 'No messages yet',
    read: 'read' as const,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  const unreadCount = allCommunications.filter(
    msg => msg.read === 'unread' && msg.direction === 'inbound'
  ).length;

  return {
    id: `${type}_${contactInfo}`,
    type,
    contact_info: contactInfo,
    messages: allCommunications,
    last_message: lastMessage,
    unread_count: unreadCount,
    has_unread: unreadCount > 0
  };
}

